/**
 * 1:1 voice call state machine. Mirrors the shape of `session.ts`: a small
 * zustand store for UI-reactive state, plus module-level (non-reactive)
 * handles for the `RTCPeerConnection` and local media stream.
 *
 * Signaling flows through `messaging.ts`'s `sendCallSignal`/`onCallSignal`
 * (MLS-encrypted, same channel as chat). This module never touches the
 * network directly and never touches crypto directly.
 */
import { create } from "zustand";
import { CALL_RING_TTL_MS, DELIVERED_TTL_MS } from "@truewave/protocol";
import type { CallSignalPayload } from "../calling/signaling.js";
import { CallConnection, getIceServers, getLocalStream, stopStream, STUN_ONLY } from "../calling/webrtc.js";
import { playRingback, playRingtone, vibrateRinging, type Tone } from "../calling/tones.js";
import { keepAwake } from "../calling/wakeLock.js";
import { isIosDevice, setAudioSessionType } from "../calling/callAudio.js";
import { closeNotifications, showLocalNotification } from "../notifications.js";
import { messaging } from "../services/messaging.js";
import { getContact, insertCallLog, type CallLogEntry } from "../storage/db.js";

export type CallPhase = "idle" | "outgoing" | "incoming" | "connected" | "ended";
type EndReason = "hangup" | "decline" | "cancel" | "failed" | "busy";

/**
 * Races `promise` against a timeout so a stuck step (a hung getUserMedia
 * prompt, a TURN fetch that never returns, an encrypt() that never resolves)
 * fails visibly and recoverably instead of leaving the call stuck on
 * "Ringing…"/"Connecting…" forever with no error and nothing in the console.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

interface CallState {
  phase: CallPhase;
  peerUserId: string | null;
  peerUsername: string | null;
  ringingConfirmed: boolean;
  video: boolean;
  videoEnabled: boolean;
  muted: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  /** Bumped whenever the remote stream gains a track, so the UI can re-route audio. */
  remoteTracks: number;
  /** iPhone: loudspeaker (true) or top earpiece (false). See calling/callAudio.ts. */
  speaker: boolean;
  error: string | null;
  /**
   * Why the last call wouldn't start, kept (in memory only) after the notice
   * on the call screen has gone. A call failing on a phone we can't inspect is
   * otherwise only ever described as "not working".
   */
  lastCallProblem: string | null;
  /** Connected, but the network path died and we're trying to get it back. */
  reconnecting: boolean;

  startCall(peerUserId: string, peerUsername: string, video: boolean): Promise<void>;
  acceptCall(): Promise<void>;
  declineCall(): void;
  cancelCall(): void;
  hangUp(): void;
  toggleMute(): void;
  toggleVideo(): void;
  toggleSpeaker(): void;
}

// Non-reactive call state. `callId` is the source of truth for "is this signal
// about the call we're currently in" — everything else lives in the store.
let callId: string | null = null;
let direction: "in" | "out" | null = null;
let connection: CallConnection | null = null;
let localStream: MediaStream | null = null;
let pendingOffer: RTCSessionDescriptionInit | null = null;
let pendingVideo = false;
/**
 * The caller's ICE candidates that arrive while we're still ringing. The
 * caller starts gathering (and sending) candidates the moment it sends the
 * invite and is usually done within a second — long before anyone taps
 * Accept — but the callee has no RTCPeerConnection until it accepts. Without
 * holding these, every one of them was silently dropped, so the callee never
 * learned how to reach the caller: fine on one machine or LAN, but across
 * real networks (cellular, NAT, TURN) each side ended up seeing only its own
 * camera with no remote audio or video.
 */
let earlyRemoteCandidates: RTCIceCandidateInit[] = [];
let startedAt: number | null = null;
let wasConnected = false;
let tone: Tone | null = null;
let stopVibration: (() => void) | null = null;
/** Outgoing: gives up after CALL_RING_TTL_MS with nobody picking up. */
let noAnswerTimer: ReturnType<typeof setTimeout> | null = null;
/** Clears the brief "ended" notice (e.g. "No answer") back to idle. */
let noticeTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Reconnecting a call instead of hanging up. When a phone locks or switches
 * networks mid-call the media path dies; previously that ended the call the
 * moment WebRTC reported "failed". Now both sides hold the call open for up to
 * RECOVERY_WINDOW_MS while the caller keeps offering an ICE restart (queued by
 * the relay if the other phone is asleep, so it's answered as soon as that
 * phone is back on screen).
 */
/** How long to wait for the mic — the permission sheet is on the user's clock. */
const MIC_WAIT_MS = 60_000;
/**
 * Encrypting a call signal queues behind every other MLS operation (see
 * `withCrypto` in services/messaging.ts): incoming messages are decrypted one
 * at a time, in arrival order, and one handler may hold the queue for up to a
 * minute. Opening the app to a backlog and tapping Call straight away — the
 * ordinary way to place a call — therefore means waiting for that backlog
 * first. Eight seconds was a desktop-speed guess and failed the call before
 * the invite was ever encrypted; the relay holds a ringing invite for
 * CALL_RING_TTL_MS, so that is the honest deadline.
 */
const SIGNAL_WAIT_MS = CALL_RING_TTL_MS;
/** Local SDP work: no network, but a phone under load is not instant. */
const SDP_WAIT_MS = 20_000;
const RECOVERY_WINDOW_MS = 60_000;
const RESTART_RETRY_MS = 10_000;
/** A "disconnected" blip this short usually heals by itself. */
const DISCONNECT_GRACE_MS = 3_000;
let recoveryDeadline: ReturnType<typeof setTimeout> | null = null;
let restartTimer: ReturnType<typeof setInterval> | null = null;
let disconnectTimer: ReturnType<typeof setTimeout> | null = null;
/** An ICE-restart offer is waiting for its answer. */
let restartOfferPending = false;
/** Signaling says connected; this fails the call if media never follows. */
let connectWatchdog: ReturnType<typeof setTimeout> | null = null;
/** Incoming: give up ringing if the caller never says anything again. */
let ringTimeout: ReturnType<typeof setTimeout> | null = null;

function beginRecovery(): void {
  if (recoveryDeadline || !callId) return;
  const thisCall = callId;
  useCallStore.setState({ reconnecting: true });
  recoveryDeadline = setTimeout(() => {
    if (callId === thisCall) void endActive("failed", "Call dropped");
  }, RECOVERY_WINDOW_MS);
  void attemptRestart();
  restartTimer = setInterval(() => void attemptRestart(), RESTART_RETRY_MS);
}

function endRecovery(): void {
  if (recoveryDeadline) clearTimeout(recoveryDeadline);
  if (restartTimer) clearInterval(restartTimer);
  if (disconnectTimer) clearTimeout(disconnectTimer);
  recoveryDeadline = null;
  restartTimer = null;
  disconnectTimer = null;
  restartOfferPending = false;
  if (useCallStore.getState().reconnecting) useCallStore.setState({ reconnecting: false });
}

/**
 * The caller offers an ICE restart; the callee asks the caller for one.
 *
 * Only ever one offer in flight: a retry while the previous offer is still
 * unanswered used to supersede it, so the answer that eventually came back
 * (from a phone that had been asleep — exactly the case this exists for) no
 * longer matched and was rejected. The peer connection is back in "stable"
 * once an answer has been applied, which is the signal to try again.
 */
async function attemptRestart(): Promise<void> {
  const peerUserId = useCallStore.getState().peerUserId;
  if (!connection || !callId || !peerUserId) return;
  try {
    if (direction === "out") {
      if (connection.signalingState !== "stable") return; // offer still outstanding
      const sdp = await connection.createRestartOffer();
      restartOfferPending = true;
      void messaging.sendCallSignal(peerUserId, { t: "restart", callId, sdp });
    } else {
      void messaging.sendCallSignal(peerUserId, { t: "restart-request", callId });
    }
  } catch (err) {
    console.warn("call reconnect attempt failed", err);
  }
}

/**
 * The OS may have ended the microphone/camera while the phone was locked —
 * the call would then carry silence even once reconnected. Swap in fresh ones.
 */
async function refreshLocalMedia(): Promise<void> {
  if (!connection || !localStream) return;
  if (!localStream.getTracks().some((t) => t.readyState === "ended")) return;
  const { video, muted, videoEnabled } = useCallStore.getState();
  const forCall = callId;
  const previous = localStream;
  let fresh: MediaStream | null = null;
  try {
    fresh = await getLocalStream(video);
    // The call may have ended while the mic was being handed over. Anything
    // acquired after that must be released, or iOS keeps showing the orange
    // recording indicator until the app is reloaded.
    if (callId !== forCall || !connection || localStream !== previous) {
      stopStream(fresh);
      return;
    }
    for (const t of fresh.getAudioTracks()) t.enabled = !muted;
    for (const t of fresh.getVideoTracks()) t.enabled = videoEnabled;
    await connection.replaceLocalTracks(fresh);
    stopStream(previous);
    localStream = fresh;
    useCallStore.setState({ localStream: fresh });
  } catch (err) {
    stopStream(fresh);
    console.warn("couldn't restart the microphone/camera", err);
  }
}

/** Back on screen mid-call: fix the media, and reconnect straight away if needed. */
async function onCallResumed(): Promise<void> {
  if (!callId || !connection) return;
  await refreshLocalMedia();
  if (wasConnected && connection.state !== "connected") {
    if (recoveryDeadline) void attemptRestart();
    else beginRecovery();
  }
}

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void onCallResumed();
});

/**
 * `phase: "connected"` only means the answer was exchanged — media may still
 * never flow. Without this, a call that never reaches a live connection sits
 * on a running timer over silence forever (the "disconnected" handling only
 * applies once a call has connected at least once).
 */
const CONNECT_WATCHDOG_MS = 20_000;

function armConnectWatchdog(): void {
  if (connectWatchdog) clearTimeout(connectWatchdog);
  const thisCall = callId;
  connectWatchdog = setTimeout(() => {
    connectWatchdog = null;
    if (callId !== thisCall || wasConnected) return;
    if (connection?.state === "connected") return;
    console.error("call never carried media after signaling connected");
    void endActive("failed", "Couldn't connect the call");
  }, CONNECT_WATCHDOG_MS);
}

function clearCallTimers(): void {
  if (connectWatchdog) clearTimeout(connectWatchdog);
  connectWatchdog = null;
  if (ringTimeout) clearTimeout(ringTimeout);
  ringTimeout = null;
}

function stopCurrentTone(): void {
  tone?.stop();
  tone = null;
  stopVibration?.();
  stopVibration = null;
}

/**
 * Ring for an incoming call. iOS keeps the audio engine suspended until the
 * app is touched, so a call arriving on an untouched app would ring silently —
 * buzz instead, and keep checking in case a touch wakes the audio up.
 */
function startRinging(): void {
  stopCurrentTone();
  const started = playRingtone();
  tone = started;
  if (started.audible) return;
  stopVibration = vibrateRinging();
  // A tap (or returning to the app) resumes the engine — pick the ring up then.
  const poll = setInterval(() => {
    if (tone !== started) {
      clearInterval(poll);
      return;
    }
    if (started.audible) {
      clearInterval(poll);
      stopVibration?.();
      stopVibration = null;
    }
  }, 500);
}

function outcomeFor(reason: EndReason, connected: boolean): CallLogEntry["outcome"] {
  if (connected) return "completed";
  if (reason === "decline" || reason === "busy") return "declined";
  if (reason === "cancel") return "canceled";
  return "failed";
}

function teardown(): void {
  endRecovery();
  clearCallTimers();
  if (noAnswerTimer) clearTimeout(noAnswerTimer);
  noAnswerTimer = null;
  connection?.close();
  connection = null;
  earlyRemoteCandidates = [];
  for (const track of localStream?.getTracks() ?? []) track.stop();
  localStream = null;
  pendingOffer = null;
  stopCurrentTone();
}

/**
 * Log the call and return to idle. With a `notice` ("No answer", "Can't be
 * reached"), the overlay shows it for a moment first instead of vanishing
 * without explanation.
 */
async function logAndReset(reason: EndReason, notice?: string): Promise<void> {
  const peerUsername = useCallStore.getState().peerUsername;
  if (callId && direction && startedAt) {
    const endedAt = Date.now();
    await insertCallLog({
      id: callId,
      peerUserId: useCallStore.getState().peerUserId!,
      dir: direction,
      startedAt,
      endedAt,
      outcome: outcomeFor(reason, wasConnected),
      expiresAt: endedAt + DELIVERED_TTL_MS,
    });
  }
  teardown();
  callId = null;
  direction = null;
  startedAt = null;
  wasConnected = false;
  useCallStore.setState({
    phase: "idle",
    peerUserId: null,
    peerUsername: null,
    ringingConfirmed: false,
    video: false,
    videoEnabled: true,
    muted: false,
    localStream: null,
    remoteStream: null,
    remoteTracks: 0,
    error: null,
    reconnecting: false,
  }); // lastCallProblem deliberately survives: it outlives the call it describes.

  if (noticeTimer) clearTimeout(noticeTimer);
  noticeTimer = null;
  if (notice) {
    useCallStore.setState({ phase: "ended", peerUsername, error: notice });
    noticeTimer = setTimeout(
      () => {
        noticeTimer = null;
        if (useCallStore.getState().phase === "ended") {
          useCallStore.setState({ phase: "idle", peerUsername: null, error: null });
        }
      },
      // "Call ended" is just an acknowledgement; something that went wrong has
      // to be read and acted on.
      reason === "failed" ? 6000 : 2500,
    );
  }
}

async function setUpConnection(video: boolean): Promise<CallConnection> {
  const forCall = callId;
  // withTimeout can only stop *waiting* for getUserMedia, it can't cancel it,
  // and the permission sheet legitimately takes as long as the user takes. The
  // stream that arrives once we have given up waiting is the one that would
  // otherwise keep the microphone open for good (the stuck orange recording
  // indicator on iPhone), so release exactly that one. It can't be done by
  // watching the promise: that callback runs *before* this await resumes, so
  // it can't yet tell a stream the call is about to use from one nobody
  // wants — and it stopped the microphone of every healthy call.
  // iOS refuses capture outright while the audio session is in a playback
  // category — "InvalidStateError: AudioSession category is not compatible
  // with audio capture" — so this has to be claimed *before* getUserMedia,
  // never after it. An outgoing call sets its phase (and with it the ringing
  // category) before the microphone is ever opened, which is what made every
  // outgoing call, and every answer, fail the moment the ringtone work landed.
  if (isIosDevice()) setAudioSessionType("play-and-record");
  const pending = getLocalStream(video);
  let stream: MediaStream;
  try {
    stream = await withTimeout(pending, MIC_WAIT_MS, "getUserMedia");
  } catch (err) {
    void pending.then(stopStream, () => undefined);
    throw err;
  }
  if (callId !== forCall) {
    stopStream(stream);
    throw new Error("call ended before the microphone was ready");
  }
  localStream = stream;
  useCallStore.setState({ localStream });
  const account = messaging.account;
  // Relayed candidates make a call work across awkward networks, but a slow
  // credential fetch must not take the call down with it: public STUN alone
  // still connects most of the time.
  const iceServers = account
    ? await withTimeout(getIceServers(account), 8_000, "fetching TURN credentials").catch((err: unknown) => {
        console.warn("falling back to STUN only", err);
        return STUN_ONLY;
      })
    : [];
  const conn = new CallConnection(iceServers, {
    onIceCandidate: (candidate) => {
      if (callId) void messaging.sendCallSignal(useCallStore.getState().peerUserId!, { t: "ice", callId, candidate });
    },
    onRemoteStream: (stream) => useCallStore.setState({ remoteStream: stream, remoteTracks: stream.getTracks().length }),
    onStateChange: (state) => {
      if (state === "connected") {
        wasConnected = true;
        if (connectWatchdog) clearTimeout(connectWatchdog);
        connectWatchdog = null;
        endRecovery();
      } else if (state === "disconnected" && wasConnected) {
        disconnectTimer ??= setTimeout(() => {
          disconnectTimer = null;
          if (connection && connection.state !== "connected") beginRecovery();
        }, DISCONNECT_GRACE_MS);
      } else if (state === "failed") {
        if (wasConnected) {
          beginRecovery();
        } else {
          console.error("call connection failed before it ever connected");
          void endActive("failed", "Couldn't connect the call");
        }
      }
    },
  });
  conn.addLocalStream(localStream);
  return conn;
}

/**
 * Why a call couldn't be set up, in words the user can act on. "Couldn't start
 * the call" on its own sends people looking for a network problem when the
 * real answer is usually a microphone they (or iOS, remembering an earlier
 * "Don't Allow") won't hand over.
 */
/**
 * Keep the technical cause where it can be read back later: the notice on the
 * call screen is gone in seconds, and this is the only thing that tells us
 * which step failed on a device we can't attach a console to.
 */
function recordCallProblem(err: unknown, notice: string): void {
  const name = typeof err === "object" && err !== null ? String((err as { name?: unknown }).name ?? "") : "";
  const message = err instanceof Error ? err.message : String(err);
  const detail = name && name !== "Error" ? `${name}: ${message}` : message;
  const at = new Date().toLocaleTimeString();
  useCallStore.setState({ lastCallProblem: `${at} — ${notice} (${detail})` });
}

export function callFailureNotice(err: unknown, fallback: string): string {
  const name = typeof err === "object" && err !== null ? String((err as { name?: unknown }).name ?? "") : "";
  const message = err instanceof Error ? err.message : "";
  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
      return "Allow microphone access, then call again";
    case "NotFoundError":
    case "OverconstrainedError":
      return "No microphone on this device";
    case "NotReadableError":
    case "AbortError":
      return "The microphone is busy in another app";
    default: {
      if (message.startsWith("getUserMedia timed out")) return "The microphone never opened";
      // "<label> timed out after <n>ms" — the label says which step gave up,
      // which is the one thing worth putting on screen when a call won't go.
      const timedOut = /^(.+) timed out after /.exec(message);
      if (timedOut) return `${fallback} — ${timedOut[1]} timed out`;
      return fallback;
    }
  }
}

/** Anything other than idle / the brief "ended" notice. */
export function callInProgress(): boolean {
  const phase = useCallStore.getState().phase;
  return phase === "incoming" || phase === "outgoing" || phase === "connected";
}

/** End whatever call is active (if any) for a reason we originated locally. */
async function endActive(reason: EndReason, notice?: string): Promise<void> {
  if (!callId || !direction) return;
  const peerUserId = useCallStore.getState().peerUserId;
  if (peerUserId) void messaging.sendCallSignal(peerUserId, { t: "end", callId, reason });
  await logAndReset(reason, notice);
}

// --- inbound signal handling -------------------------------------------------

// Signals are handled strictly one at a time, in arrival order. Each handler
// awaits (a DB lookup, applying an SDP answer), and letting them overlap meant
// an ICE candidate right behind an invite could be checked against a callId
// the invite handler hadn't set yet — and get thrown away.
let signalQueue: Promise<void> = Promise.resolve();
messaging.onCallSignal((from, payload: CallSignalPayload) => {
  signalQueue = signalQueue
    .then(() => handleSignal(from, payload))
    .catch((err: unknown) => console.error("handling call signal failed", payload.t, err));
});

async function handleSignal(from: string, payload: CallSignalPayload): Promise<void> {
  if (payload.t === "invite") {
    if (callId !== null) {
      // Already in a call — busy.
      void messaging.sendCallSignal(from, { t: "end", callId: payload.callId, reason: "busy" });
      return;
    }
    // Claim the call before any await, so signals for it are recognized at once.
    callId = payload.callId;
    direction = "in";
    startedAt = Date.now();
    pendingOffer = payload.sdp;
    pendingVideo = payload.video;
    earlyRemoteCandidates = [];
    useCallStore.setState({
      phase: "incoming",
      peerUserId: from,
      peerUsername: from.slice(0, 8),
      video: payload.video,
    });
    stopCurrentTone();
    startRinging();
    void showLocalNotification("call");
    void messaging.sendCallSignal(from, { t: "ringing", callId: payload.callId });
    // Stop ringing if the caller goes quiet. Their "end" can't arrive if they
    // crashed or lost signal, and without this the call would ring forever and
    // leave this side permanently "in a call" — answering every later call busy.
    if (ringTimeout) clearTimeout(ringTimeout);
    ringTimeout = setTimeout(() => {
      ringTimeout = null;
      if (callId === payload.callId && useCallStore.getState().phase === "incoming") {
        void logAndReset("cancel", "Missed call");
      }
    }, CALL_RING_TTL_MS);
    const contact = await getContact(from);
    if (contact && callId === payload.callId) useCallStore.setState({ peerUsername: contact.username });
    return;
  }

  // Everything else only matters for the call we're currently in.
  if (payload.callId !== callId || from !== useCallStore.getState().peerUserId) return;

  switch (payload.t) {
    case "ringing":
      useCallStore.setState({ ringingConfirmed: true });
      break;
    case "accept":
      if (connection) {
        await connection.applyAnswer(payload.sdp);
        stopCurrentTone();
        useCallStore.setState({ phase: "connected" });
        armConnectWatchdog();
      }
      break;
    case "ice":
      if (connection) await connection.addRemoteIceCandidate(payload.candidate);
      else earlyRemoteCandidates.push(payload.candidate);
      break;
    case "restart":
      // The caller is reconnecting the call (e.g. we just came back from a
      // locked screen): answer with our fresh network paths.
      if (connection) {
        const sdp = await connection.answerRestart(payload.sdp);
        void messaging.sendCallSignal(from, { t: "restart-answer", callId: payload.callId, sdp });
      }
      break;
    case "restart-answer":
      // Ignore an answer to an offer we replaced, or never sent.
      if (connection && direction === "out" && restartOfferPending) {
        restartOfferPending = false;
        try {
          await connection.applyAnswer(payload.sdp);
        } catch (err) {
          console.warn("couldn't apply the reconnect answer", err);
        }
      }
      break;
    case "restart-request":
      // The callee noticed the break first — drive recovery from here, so this
      // side also gets the "Reconnecting…" UI, the retries and the deadline.
      if (direction === "out") {
        if (recoveryDeadline) void attemptRestart();
        else beginRecovery();
      }
      break;
    case "end":
      await logAndReset(payload.reason);
      break;
  }
}

// --- store --------------------------------------------------------------

export const useCallStore = create<CallState>((set, get) => ({
  phase: "idle",
  peerUserId: null,
  peerUsername: null,
  ringingConfirmed: false,
  video: false,
  videoEnabled: true,
  muted: false,
  localStream: null,
  remoteStream: null,
  remoteTracks: 0,
  speaker: true,
  error: null,
  lastCallProblem: null,
  reconnecting: false,

  async startCall(peerUserId, peerUsername, video) {
    const phase = get().phase;
    // A call that was never cleaned up (the app was locked while it rang, and
    // the peer's "end" expired at the relay before we came back) used to leave
    // callId set forever — after which every later call was answered "busy"
    // and this bailed out. Clear anything that can no longer be live.
    if (callId !== null && !wasConnected && startedAt !== null && Date.now() - startedAt > CALL_RING_TTL_MS) {
      await logAndReset("cancel");
    }
    if (phase !== "idle" && phase !== "ended") return;
    const thisCall = crypto.randomUUID();
    callId = thisCall;
    direction = "out";
    startedAt = Date.now();
    set({ phase: "outgoing", peerUserId, peerUsername, video, error: null });
    stopCurrentTone();
    tone = playRingback();

    try {
      connection = await setUpConnection(video);
      const offer = await withTimeout(connection.createOffer(), SDP_WAIT_MS, "creating the offer");
      const { live, pushable } = await withTimeout(
        messaging.sendCallSignal(peerUserId, { t: "invite", callId: thisCall, video, sdp: offer }, "call"),
        SIGNAL_WAIT_MS,
        "sending the call invite",
      );
      if (callId !== thisCall) return; // canceled meanwhile
      if (!live && !pushable) {
        // Not connected, and no notification can reach them (they never
        // turned notifications on, or their browser can't get them).
        await logAndReset("failed", "Can't be reached right now");
        return;
      }
      // Either their app is open, or an "Incoming call" notification is on its
      // way and they can still open the app and pick up. Ring for as long as
      // the relay holds the invite, then give up like a phone would.
      noAnswerTimer = setTimeout(() => {
        if (callId === thisCall && get().phase === "outgoing") void endActive("cancel", "No answer");
      }, CALL_RING_TTL_MS);
    } catch (err) {
      console.error("startCall failed", err);
      const notice = callFailureNotice(err, "Couldn't start the call");
      recordCallProblem(err, notice);
      if (callId === thisCall) await logAndReset("failed", notice);
    }
  },

  async acceptCall() {
    const { phase, peerUserId } = get();
    if (phase !== "incoming" || !callId || !pendingOffer || !peerUserId) return;
    stopCurrentTone();
    try {
      connection = await setUpConnection(pendingVideo);
      // Hand over the candidates that arrived while ringing. CallConnection
      // holds them until the offer is applied in createAnswer, then adds them.
      for (const candidate of earlyRemoteCandidates.splice(0)) {
        await connection.addRemoteIceCandidate(candidate);
      }
      const answer = await withTimeout(connection.createAnswer(pendingOffer), SDP_WAIT_MS, "creating the answer");
      void withTimeout(
        messaging.sendCallSignal(peerUserId, { t: "accept", callId, sdp: answer }),
        SIGNAL_WAIT_MS,
        "sending the call accept",
      ).catch((err: unknown) => console.error("sending accept failed", err));
      set({ phase: "connected" });
      armConnectWatchdog();
    } catch (err) {
      console.error("acceptCall failed", err);
      const notice = callFailureNotice(err, "Couldn't answer the call");
      recordCallProblem(err, notice);
      await endActive("failed", notice);
    }
  },

  declineCall() {
    void endActive("decline");
  },

  cancelCall() {
    void endActive("cancel");
  },

  hangUp() {
    void endActive("hangup");
  },

  toggleMute() {
    const muted = !get().muted;
    if (localStream) connection?.setMuted(muted, localStream);
    set({ muted });
  },

  toggleSpeaker() {
    set({ speaker: !get().speaker });
  },

  toggleVideo() {
    const videoEnabled = !get().videoEnabled;
    if (localStream) connection?.setVideoEnabled(videoEnabled, localStream);
    set({ videoEnabled });
  },
}));

// Keep the screen from locking for the whole call (the most common way a call
// used to drop), and take down the "Incoming call" notification once the app
// itself is handling the call.
useCallStore.subscribe((state, prev) => {
  if (state.phase === prev.phase) return;
  const ringing = state.phase === "incoming" || state.phase === "outgoing";
  const inCall = ringing || state.phase === "connected";
  keepAwake(inCall);
  if (isIosDevice()) {
    // A ringing *incoming* call is the one moment with sound and no
    // microphone, so it asks for plain playback: loud, through the
    // loudspeaker. An outgoing call is excluded deliberately — it opens the
    // microphone immediately, and iOS will not capture from a playback
    // category, so setUpConnection claims play-and-record instead.
    if (state.phase === "incoming") setAudioSessionType("playback");
    else if (!inCall) setAudioSessionType("auto");
  }
  // Each new call starts on the loudspeaker.
  if (state.phase === "idle" && !state.speaker) useCallStore.setState({ speaker: true });
  void closeNotifications("truewave-call");
});
